import React, { useState, useRef, useEffect } from 'react';
import { useOrg } from '../context/OrgContext';

export function OrgSelector() {
  const { orgs, selectedOrg, selectOrg, hasMultipleOrgs } = useOrg();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      // Focus after the dropdown has actually rendered.
      const id = setTimeout(() => searchInputRef.current && searchInputRef.current.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  if (!selectedOrg) {
    return null;
  }

  const filteredOrgs = searchQuery.trim()
    ? orgs.filter(org => org.name.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : orgs;

  if (!hasMultipleOrgs) {
    return (
      <div style={styles.singleOrg}>
        <span style={styles.orgName}>{selectedOrg.name}</span>
        <span style={styles.roleBadge}>{selectedOrg.role}</span>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={styles.trigger}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span style={styles.orgName}>{selectedOrg.name}</span>
        <span style={styles.roleBadge}>{selectedOrg.role}</span>
        <svg
          style={{ ...styles.chevron, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.searchWrap}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIsOpen(false);
              }}
              placeholder="Search organizations..."
              style={styles.searchInput}
              aria-label="Search organizations"
            />
          </div>
          <ul style={styles.list} role="listbox">
            {filteredOrgs.length === 0 ? (
              <li style={styles.emptyState}>No organizations match "{searchQuery}"</li>
            ) : (
              filteredOrgs.map((org) => (
                <li key={org.id} role="option" aria-selected={org.id === selectedOrg.id}>
                  <button
                    onClick={() => {
                      selectOrg(org.id);
                      setIsOpen(false);
                    }}
                    style={{
                      ...styles.option,
                      backgroundColor: org.id === selectedOrg.id ? '#edf2f7' : 'transparent'
                    }}
                  >
                    <span style={styles.optionName}>{org.name}</span>
                    <span style={styles.optionRole}>{org.role}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative'
  },
  singleOrg: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.875rem'
  },
  orgName: {
    fontWeight: '500'
  },
  roleBadge: {
    fontSize: '0.75rem',
    padding: '0.125rem 0.375rem',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    textTransform: 'capitalize'
  },
  chevron: {
    marginLeft: '0.25rem',
    transition: 'transform 0.2s'
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    minWidth: '240px',
    maxWidth: '320px',
    // Caps the whole popover to a viewport-relative height so it can never
    // run off the bottom of the screen, however many orgs there are — the
    // list below scrolls internally instead.
    maxHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    zIndex: 100,
    overflow: 'hidden'
  },
  searchWrap: {
    padding: '0.5rem',
    borderBottom: '1px solid #edf2f7',
    flexShrink: 0
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.5rem 0.625rem',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '0.875rem',
    color: '#1a202c',
    outline: 'none'
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: '0.5rem',
    overflowY: 'auto',
    // Required for the scrollbar to actually engage inside a height-capped
    // flex container — without these a flex item won't shrink below its
    // content size, so this would just grow past maxHeight instead.
    flex: 1,
    minHeight: 0
  },
  emptyState: {
    padding: '0.75rem',
    fontSize: '0.875rem',
    color: '#718096',
    textAlign: 'center'
  },
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0.625rem 0.75rem',
    border: 'none',
    background: 'transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#1a202c',
    textAlign: 'left'
  },
  optionName: {
    fontWeight: '500'
  },
  optionRole: {
    fontSize: '0.75rem',
    color: '#718096',
    textTransform: 'capitalize'
  }
};
